<template>
  <div class="cma-dashboard">
    <header class="cma-dashboard__header">
      <img
        class="cma-dashboard__logo"
        src="/static/imgs/cma-logo-mark.svg"
        alt=""
        aria-hidden="true"
      >

      <div class="cma-dashboard__header-main">
        <div class="cma-dashboard__title-row">
          <h1 class="cma-dashboard__title">{{ assetTitle }}</h1>
          <span
            v-if="serialNumberLabel"
            class="cma-dashboard__serial-number"
          >{{ serialNumberLabel }}</span>
        </div>
        <ul
          v-if="assetTags.length || loadingAssetTags"
          class="cma-dashboard__tags"
        >
          <template v-if="loadingAssetTags">
            <li
              v-for="def in assetTagDefs"
              :key="`tag-skeleton-${def.id}`"
              class="cma-dashboard__tag"
            >
              <strong>{{ $t(def.labelKey) }}:</strong>
              <span class="cma-skeleton-bar cma-dashboard__tag-skeleton" />
            </li>
          </template>
          <template v-else>
            <li
              v-for="tag in assetTags"
              :key="tag.id"
              class="cma-dashboard__tag"
            >
              <strong>{{ $t(tag.labelKey) }}:</strong> {{ tag.value }}
            </li>
          </template>
        </ul>
      </div>

      <div class="cma-dashboard__header-actions">
        <button
          v-b-tooltip.hover.bottom="$t('cma.rotation.toggle')"
          type="button"
          class="cma-dashboard__rotation-toggle-button"
          :aria-label="$t('cma.rotation.toggle')"
          :aria-pressed="String(rotationEnabled)"
          @click="onRotationToggle(!rotationEnabled)"
        >
          <span
            class="material-icons-round"
            aria-hidden="true"
          >{{ rotationEnabled ? 'pause' : 'play_arrow' }}</span>
        </button>

        <button
          v-b-tooltip.hover.bottom="$t('buttons.refresh')"
          type="button"
          class="cma-dashboard__refresh-button"
          :aria-label="$t('buttons.refresh')"
          @click="onRefreshTick"
        >
          <span
            class="material-icons-round"
            aria-hidden="true"
          >sync</span>
        </button>

        <!-- desativado por enquanto (ver onSaveFramingClick/logCameraFraming)
        <button
          type="button"
          class="cma-dashboard__save-framing-button"
          :aria-label="$t('cma.header.save-framing')"
          @click="onSaveFramingClick"
        >
          <span
            class="material-icons-round"
            aria-hidden="true"
          >save</span>
        </button>
        -->

        <!-- mesmo icone e mesmo rotulo da tela de monitoramento (ver
             components/header/IconsHeader.vue, item `buttons.active-info`) -->
        <button
          v-b-tooltip.hover.bottom="$t('buttons.active-info')"
          type="button"
          class="cma-dashboard__asset-info-button"
          :class="{ 'cma-dashboard__asset-info-button--active': assetInfoExpanded }"
          :aria-label="$t('buttons.active-info')"
          :aria-expanded="String(assetInfoExpanded)"
          @click="onAssetInfoIconClick"
        >
          <span
            class="material-icons-round"
            aria-hidden="true"
          >info</span>
        </button>

        <s-sidebar
          class="cma-dashboard__filter"
          title-label="cma.header.filter-title"
          subtitle-label="cma.header.filter-subtitle"
          side="right"
          @on-show="onFilterShow"
          @on-hide="onFilterHide"
          @on-cancel="onFilterHide"
        >
          <template #body>
            <treeview
              :tree="assetTree"
              :selected-id="selectedAssetId"
              @onClickNode="onTreeNodeClick"
            />
          </template>
        </s-sidebar>
      </div>
    </header>

    <div class="cma-dashboard__hub">
      <div
        ref="hub"
        class="cma-dashboard__canvas"
        :class="{ 'cma-dashboard__canvas--with-aside': assetInfoExpanded }"
        @click="onCanvasClick"
      >
        <svg
          class="cma-dashboard__connectors"
          :class="{ 'cma-dashboard__connectors--hidden': switching || exploded }"
          aria-hidden="true"
        >
          <g
            v-for="connector in connectorPaths"
            :key="connector.id"
            :class="connectorClass(connector)"
          >
            <path
              class="cma-dashboard__connector-line"
              :d="connector.path"
            />
            <template v-if="connector.visible">
              <circle
                class="cma-dashboard__connector-halo"
                :cx="connector.endX"
                :cy="connector.endY"
                r="6"
              />
              <circle
                class="cma-dashboard__connector-dot"
                :cx="connector.endX"
                :cy="connector.endY"
                r="2.5"
              />
            </template>
          </g>
        </svg>

        <template v-if="selectedAssetId">
          <div class="cma-dashboard__card-column cma-dashboard__card-column--left">
            <cma-reading-card
              v-if="cardsById.bushings"
              ref="card-bushings"
              class="cma-dashboard__card cma-dashboard__card--bushings"
              :card="cardsById.bushings"
              :active="selectedCardId === 'bushings'"
              :loading="!readings"
              :skeleton-rows="cardsById.bushings.skeletonRows"
              @mouseenter.native="hoveredCardId = 'bushings'"
              @mouseleave.native="onCardMouseLeave('bushings')"
              @click.native.stop="onCardClick('bushings')"
            />
            <cma-reading-card
              v-if="cardsById['gas-and-humidity']"
              ref="card-gas-and-humidity"
              class="cma-dashboard__card cma-dashboard__card--gas-and-humidity"
              :card="cardsById['gas-and-humidity']"
              :active="selectedCardId === 'gas-and-humidity'"
              :loading="!readings"
              :skeleton-rows="cardsById['gas-and-humidity'].skeletonRows"
              @mouseenter.native="hoveredCardId = 'gas-and-humidity'"
              @mouseleave.native="onCardMouseLeave('gas-and-humidity')"
              @click.native.stop="onCardClick('gas-and-humidity')"
            />
            <cma-reading-card
              v-if="cardsById.temperatures"
              ref="card-temperatures"
              class="cma-dashboard__card cma-dashboard__card--temperatures"
              :card="cardsById.temperatures"
              :active="selectedCardId === 'temperatures'"
              :loading="!readings"
              :skeleton-rows="cardsById.temperatures.skeletonRows"
              @mouseenter.native="hoveredCardId = 'temperatures'"
              @mouseleave.native="onCardMouseLeave('temperatures')"
              @click.native.stop="onCardClick('temperatures')"
            />
            <cma-reading-card
              v-if="cardsById['load-voltage']"
              ref="card-load-voltage"
              class="cma-dashboard__card cma-dashboard__card--load-voltage"
              :card="cardsById['load-voltage']"
              :active="selectedCardId === 'load-voltage'"
              :loading="!readings"
              :skeleton-rows="cardsById['load-voltage'].skeletonRows"
              @mouseenter.native="hoveredCardId = 'load-voltage'"
              @mouseleave.native="onCardMouseLeave('load-voltage')"
              @click.native.stop="onCardClick('load-voltage')"
            />
          </div>
        </template>

        <div class="cma-dashboard__asset-image">
          <!--
            `key` fixo, não porque este componente entra/sai do template (ele
            não entra: não há v-if nele), mas porque ele fica ENTRE dois
            blocos `<template v-if="selectedAssetId">` (colunas de cards,
            acima/abaixo) que alternam entre um comentário (placeholder de
            v-if falso) e uma div de verdade assim que `loadAssetTree()`
            resolve. Sem key, o diff sem chave do Vue casa vnodes por TAG
            (div == div, sem olhar classe) e pode confundir a div desta
            ilustração com a das colunas vizinhas nessa transição, destruindo
            e recriando o CmaAsset3dView à toa — o que reinicia um download
            de ~20 MB + parse do CAD que já estava em andamento. Ver
            docs/specs/cma-loading-3d-diagnostico.md.
          -->
          <cma-asset-3d-view
            key="cma-asset-3d-view"
            ref="illustration"
            class="cma-dashboard__asset-illustration"
            :model-id="modelId"
            :asset-index="selectedAssetIndex"
            :highlighted-part-id="highlightedPartId"
            :asset-label="assetTitle"
            :insights="cadLoadingInsights"
            @anchors="onViewerAnchors"
            @switching="onViewerSwitching"
            @exploded="onViewerExploded"
          />
        </div>

        <div class="cma-dashboard__stage-controls">
          <button
            type="button"
            class="cma-dashboard__explode-button"
            :class="{ 'cma-dashboard__explode-button--active': exploded }"
            :aria-pressed="String(exploded)"
            @click.stop="onExplodeClick"
          >
            <span
              class="material-icons-round"
              aria-hidden="true"
            >{{ exploded ? 'close_fullscreen' : 'open_in_full' }}</span>
            {{ $t(exploded ? 'cma.actions.assemble' : 'cma.actions.explode') }}
          </button>
        </div>

        <template v-if="selectedAssetId">
          <div class="cma-dashboard__card-column cma-dashboard__card-column--right">
            <cma-reading-card
              v-if="cardsById.bag"
              ref="card-bag"
              class="cma-dashboard__card cma-dashboard__card--bag"
              :card="cardsById.bag"
              :active="selectedCardId === 'bag'"
              :loading="!readings"
              :skeleton-rows="cardsById.bag.skeletonRows"
              @mouseenter.native="hoveredCardId = 'bag'"
              @mouseleave.native="onCardMouseLeave('bag')"
              @click.native.stop="onCardClick('bag')"
            />
            <cma-reading-card
              v-if="cardsById.commutator"
              ref="card-commutator"
              class="cma-dashboard__card cma-dashboard__card--commutator"
              :card="cardsById.commutator"
              :active="selectedCardId === 'commutator'"
              :loading="!readings"
              :skeleton-rows="cardsById.commutator.skeletonRows"
              @mouseenter.native="hoveredCardId = 'commutator'"
              @mouseleave.native="onCardMouseLeave('commutator')"
              @click.native.stop="onCardClick('commutator')"
            />
            <cma-reading-card
              v-if="cardsById['electrical-data']"
              ref="card-electrical-data"
              class="cma-dashboard__card cma-dashboard__card--electrical-data"
              :card="cardsById['electrical-data']"
              :active="selectedCardId === 'electrical-data'"
              :loading="!readings"
              :skeleton-rows="cardsById['electrical-data'].skeletonRows"
              @mouseenter.native="hoveredCardId = 'electrical-data'"
              @mouseleave.native="onCardMouseLeave('electrical-data')"
              @click.native.stop="onCardClick('electrical-data')"
            />
            <cma-reading-card
              v-if="cardsById.cooling"
              ref="card-cooling"
              class="cma-dashboard__card cma-dashboard__card--cooling"
              :card="cardsById.cooling"
              :active="selectedCardId === 'cooling'"
              :loading="!readings"
              :skeleton-rows="cardsById.cooling.skeletonRows"
              @mouseenter.native="hoveredCardId = 'cooling'"
              @mouseleave.native="onCardMouseLeave('cooling')"
              @click.native.stop="onCardClick('cooling')"
            />
          </div>
        </template>
      </div>

      <cma-asset-info-panel
        v-if="selectedAssetId"
        class="cma-dashboard__asset-info"
        :expanded="assetInfoExpanded"
        :loading="loadingAssetInfo"
        :transformer-data="transformerData"
        :registration-data="registrationData"
        :cooling-stages-data="coolingStagesData"
        @toggle="onAssetInfoToggle"
      />
    </div>

    <footer
      v-if="readings || rotating"
      class="cma-dashboard__footer"
    >
      <span class="cma-dashboard__update-indicator">
        <span
          class="material-icons-round cma-dashboard__update-icon"
          aria-hidden="true"
        >event</span>
        {{ $t('cma.footer.auto-refresh') }} · {{ refreshIntervalLabel }}
        | {{ $t('cma.footer.last-update') }}: {{ lastUpdateTime }}
      </span>

      <span
        v-if="rotating"
        class="cma-dashboard__rotation-indicator"
      >
        <span
          class="material-icons-round cma-dashboard__rotation-icon"
          aria-hidden="true"
        >autorenew</span>
        <span aria-hidden="true">
          {{ rotationStatusText }}
          · {{ $t('cma.rotation.next', { time: rotationCountdownLabel }) }}
        </span>
        <span class="sr-only">{{ $t('cma.rotation.active') }}</span>
      </span>
    </footer>
  </div>
</template>

<script>
import { TreeviewLinkType } from 'sigma-mappings';
import CmaDashboardService from '@/domain/services/cma/cmaDashboardService';
import AssetListService from '@/domain/services/generalPanel/assetsList/assetsListService';
import { buildCards, buildSkeletonCards } from '@/domain/data/cma/cardsConfig';
import { modelIdForAssetIndex } from '@/domain/data/cma/transformer3dModels';
import { buildAssetTags, ASSET_TAG_DEFS } from '@/domain/data/cma/assetTags';
import {
  flattenAssetNodes, findFirstAssetNode, filterTransformerAssets,
} from '@/domain/data/cma/assetTreeAdapter';
import { nextRotationId } from '@/domain/data/cma/assetRotation';
import { Interval } from '@/services/timer';
import Treeview from '@/components/treeview/Treeview';
import CmaReadingCard from './CmaReadingCard';
import CmaAssetInfoPanel from './CmaAssetInfoPanel';
import CmaAsset3dView from './CmaAsset3dView';

const REFRESH_INTERVAL_MS = 60000;
const IDLE_ROTATION_DELAY_MS = 60000;
const ROTATION_DWELL_MS = 60000;
const POINTER_ACTIVITY_THRESHOLD_PX = 24;
// Ao ligar o rodízio (botão play) com a vista explodida: não retrai na hora
// do clique — pedido de produto, dá tempo de o usuário ainda notar que
// apertou o botão antes da peça começar a voltar pro lugar.
const ROTATION_PLAY_RETRACT_DELAY_MS = 3000;

// Mesmo padrão do "fixar" do painel de informações (CmaAssetInfoPanel.vue,
// PINNED_STORAGE_KEY): chave direta, try/catch, degrada em silêncio sem
// gravar se o localStorage não estiver disponível (navegação privada,
// cookies bloqueados em iframe) — preferência de tela, não dado de operação.
const ROTATION_ENABLED_STORAGE_KEY = 'cma-asset-rotation-enabled';

function readRotationEnabled() {
  try {
    return localStorage.getItem(ROTATION_ENABLED_STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

function writeRotationEnabled(enabled) {
  try {
    localStorage.setItem(ROTATION_ENABLED_STORAGE_KEY, String(enabled));
  } catch (e) {
    // ver comentário acima — segue sem gravar
  }
}

const CONNECTOR_BEND_RATIO = 0.42;

// `part` é o id da ÂNCORA em ANCHOR_DEFS (CmaAsset3dView.vue), não o da peça
// do modelo: a âncora `resfriamento`, por exemplo, mora na peça `radiadorB`.
//
// IMPORTANTE: `part` acompanha o CONTEÚDO exibido na posição (ver
// CONTENT_OVERRIDES em cardsConfig.js), não a posição em si — por isso as
// posições 'temperatures' e 'load-voltage' têm o `part` "trocado" aqui: a
// posição 'temperatures' EXIBE o conteúdo de gás/umidade (então aponta pra
// âncora 'gas', o relé de gás), e a posição 'load-voltage' EXIBE o conteúdo
// de temperaturas (então aponta pra âncora 'temperaturas' — chamava-se
// 'tensaoCarga', nome que sobrou do card errado; corrigido junto com
// ANCHORS_CAD em transformadorCad.js).
const CONNECTOR_DEFS = [
  { id: 'bushings', anchor: 'right', part: 'buchas' },
  { id: 'gas-and-humidity', anchor: 'right', part: 'comutador' },
  { id: 'temperatures', anchor: 'right', part: 'gas' },
  { id: 'load-voltage', anchor: 'right', part: 'temperaturas' },
  { id: 'bag', anchor: 'left', part: 'bolsa' },
  { id: 'commutator', anchor: 'left', part: 'tensao' },
  { id: 'electrical-data', anchor: 'left', part: 'eletricos' },
  { id: 'cooling', anchor: 'left', part: 'resfriamento' },
];

export default {
  name: 'CmaDashboard',
  components: {
    Treeview,
    CmaReadingCard,
    CmaAssetInfoPanel,
    CmaAsset3dView,
  },
  props: {
    // Id do ativo vindo da URL (ver generalPanel/config/routes.js,
    // `painel/visao-ativos/:assetId?`) — usado para restaurar o ativo selecionado ao
    // recarregar a tela. Ausente (`null`) quando a rota é acessada sem id;
    // nesse caso, e também se o id não existir na árvore, cai no 1º ativo da
    // hierarquia (ver loadAssetTree/resolveSelectedAssetId abaixo).
    assetId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedAssetId: null,
      assetTree: [],
      readings: null,
      refreshInterval: null,
      lastUpdateAt: null,
      connectorPaths: [],
      hoveredCardId: null,
      selectedCardId: null,
      anchors: [],
      switching: false,
      exploded: false,
      assetInfoExpanded: false,
      rotationEnabled: readRotationEnabled(),
      filterOpen: false,
      rotating: false,
      rotationIndex: -1,
      rotationRemainingSec: 0,
      savedFramings: {},
      registrationData: null,
      transformerData: null,
      coolingStagesData: null,
      loadingAssetInfo: false,
    };
  },
  computed: {
    assets() {
      return flattenAssetNodes(this.assetTree);
    },
    selectedAsset() {
      return this.assets.find(asset => asset.id === this.selectedAssetId);
    },
    assetTitle() {
      const name = this.selectedAsset ? this.selectedAsset.name : '';
      const { type } = this.transformerData || {};
      const suffix = type
        ? this.$t(`enums.transformer-type.${type}`)
        : this.$t('cma.header.asset-type-suffix');
      return name ? `${name} — ${suffix}` : suffix;
    },
    serialNumberLabel() {
      const serialNumber = this.registrationData && this.registrationData.serialNumber;
      return serialNumber ? `${this.$t('cma.header.serial-number-prefix')}: ${serialNumber}` : '';
    },
    refreshIntervalLabel() {
      return `${REFRESH_INTERVAL_MS / 1000}s`;
    },
    lastUpdateTime() {
      if (!this.lastUpdateAt) return '--';
      return this.lastUpdateAt.toLocaleTimeString(this.$i18n.locale);
    },
    cards() {
      return this.readings ? buildCards(this.readings) : buildSkeletonCards();
    },
    cardsById() {
      return this.cards.reduce((acc, card) => ({ ...acc, [card.id]: card }), {});
    },
    selectedAssetIndex() {
      return this.assets.findIndex(asset => asset.id === this.selectedAssetId);
    },
    modelId() {
      return modelIdForAssetIndex(this.selectedAssetIndex);
    },
    activeCardId() {
      return this.hoveredCardId || this.selectedCardId;
    },
    highlightedPartId() {
      const def = CONNECTOR_DEFS.find(item => item.id === this.activeCardId);
      return def ? def.part : null;
    },
    assetTags() {
      return buildAssetTags(this.registrationData);
    },
    /*
     * Legenda da tela de espera do CAD real (ver CmaAsset3dLoading.vue) —
     * uma linha só, com o nome do ativo que este dashboard já resolveu
     * antes do visualizador 3D existir.
     */
    cadLoadingInsights() {
      return [
        { at: 0, text: this.$t('cma.loading.connecting', { asset: this.assetTitle }) },
      ];
    },
    loadingAssetTags() {
      return Boolean(this.selectedAssetId) && this.loadingAssetInfo;
    },
    assetTagDefs() {
      return ASSET_TAG_DEFS;
    },
    canRotate() {
      return this.rotationEnabled && this.assets.length > 1 && !this.filterOpen;
    },
    rotationPositionLabel() {
      return this.rotationIndex >= 0 ? `${this.rotationIndex + 1}/${this.assets.length}` : '';
    },
    rotationCountdownLabel() {
      const minutes = Math.floor(this.rotationRemainingSec / 60);
      const seconds = this.rotationRemainingSec % 60;
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    },
    rotationStatusText() {
      const name = this.selectedAsset ? this.selectedAsset.name : '';
      return this.$t('cma.rotation.status', { name, position: this.rotationPositionLabel });
    },
  },
  created() {
    this.refreshInterval = new Interval(REFRESH_INTERVAL_MS);
    this.refreshInterval.onInterval.push(() => this.onRefreshTick());
    this.refreshInterval.start();
    this.loadAssetTree();
    this.rotationInterval = new Interval(ROTATION_DWELL_MS);
    this.rotationInterval.onInterval.push(() => this.advanceRotation());
    this.rotationInterval.onTick.push(remainingMs => this.onRotationTick(remainingMs));
    this.pointerAccumulatedPx = 0;
  },
  mounted() {
    window.addEventListener('resize', this.scheduleConnectorUpdate);
    window.addEventListener('pointerdown', this.onUserActivity, true);
    window.addEventListener('keydown', this.onUserActivity, true);
    window.addEventListener('wheel', this.onUserActivity, true);
    window.addEventListener('touchstart', this.onUserActivity, true);
    window.addEventListener('pointermove', this.onUserActivity, true);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    // rotationEnabled pode já vir `true` do localStorage (recarregar a
    // página com o rodízio ligado) — sem isso, o ícone mostraria "pause"
    // mas nenhum timer estaria de fato rodando.
    if (this.rotationEnabled) this.scheduleRotation();
  },
  beforeDestroy() {
    this.refreshInterval.stop();
    window.removeEventListener('resize', this.scheduleConnectorUpdate);
    if (this.connectorFrame) cancelAnimationFrame(this.connectorFrame);
    this.clearIdleTimer();
    this.clearRotationPlayRetractTimer();
    this.rotationInterval.stop();
    window.removeEventListener('pointerdown', this.onUserActivity, true);
    window.removeEventListener('keydown', this.onUserActivity, true);
    window.removeEventListener('wheel', this.onUserActivity, true);
    window.removeEventListener('touchstart', this.onUserActivity, true);
    window.removeEventListener('pointermove', this.onUserActivity, true);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    loadAssetTree() {
      return AssetListService.getAssetsTreeviewList().then((tree) => {
        // CMA só modela transformador (ver filterTransformerAssets) — filtra
        // ANTES de guardar em `assetTree`, que alimenta tanto o menu lateral
        // (`<treeview>`) quanto `assets`/o rodízio automático, então os dois
        // já nascem restritos sem filtro duplicado em cada consumidor.
        const transformerTree = filterTransformerAssets(
          tree,
          this.$t('enums.asset-info.asset-type.transformer'),
        );
        this.assetTree = transformerTree;
        this.selectedAssetId = this.resolveSelectedAssetId(transformerTree);
      });
    },
    // Prioriza o id da URL (prop `assetId`, ver routes.js) quando ele existe
    // de fato na árvore recém-carregada — cai no 1º ativo da hierarquia
    // (comportamento de sempre) tanto na ausência de id na URL quanto num id
    // inválido/de outra árvore (ex.: link antigo, ativo removido).
    resolveSelectedAssetId(tree) {
      const requestedAsset = this.assetId
        && flattenAssetNodes(tree).find(asset => asset.id === this.assetId);
      if (requestedAsset) return requestedAsset.id;
      const firstAsset = findFirstAssetNode(tree);
      return firstAsset ? firstAsset.id : null;
    },
    onTreeNodeClick(node) {
      if (node.clickType !== TreeviewLinkType.LINK) return;
      if (node.id === this.selectedAssetId) return;
      this.$router.push({ name: 'assets-view-panel', params: { assetId: node.id } });
    },
    onRotationToggle(checked) {
      this.rotationEnabled = checked;
      writeRotationEnabled(checked);
      if (checked) {
        this.scheduleRotation();
        this.scheduleRotationPlayRetract();
      } else {
        this.clearIdleTimer();
        this.clearRotationPlayRetractTimer();
        this.stopRotation();
      }
    },
    // Ligou o rodízio com a vista explodida: retrai um pouco depois do
    // clique (ver ROTATION_PLAY_RETRACT_DELAY_MS), não na hora — condição
    // checada só quando o timer dispara, não aqui, porque o usuário pode
    // expandir só depois de já ter apertado o play.
    scheduleRotationPlayRetract() {
      this.clearRotationPlayRetractTimer();
      this.rotationPlayRetractTimer = setTimeout(() => {
        this.rotationPlayRetractTimer = null;
        if (this.exploded) this.resetIllustrationView();
      }, ROTATION_PLAY_RETRACT_DELAY_MS);
    },
    clearRotationPlayRetractTimer() {
      if (!this.rotationPlayRetractTimer) return;
      clearTimeout(this.rotationPlayRetractTimer);
      this.rotationPlayRetractTimer = null;
    },
    onFilterShow() {
      this.filterOpen = true;
      if (this.rotating) {
        this.stopRotation();
      } else {
        this.clearIdleTimer();
      }
    },
    onFilterHide() {
      this.filterOpen = false;
      this.scheduleRotation();
    },
    // `movementX`/`movementY` já vêm acumulados por evento do próprio
    // navegador desde o último `pointermove` — soma-se em módulo até
    // estourar o limiar (spec §3.7); some o "toque leve na mesa" (poucos
    // px por evento) sem exigir um evento único acima do limiar.
    onUserActivity(event) {
      if (event.type === 'pointermove') {
        this.pointerAccumulatedPx += Math.abs(event.movementX || 0)
          + Math.abs(event.movementY || 0);
        if (this.pointerAccumulatedPx < POINTER_ACTIVITY_THRESHOLD_PX) return;
        this.pointerAccumulatedPx = 0;
      }
      if (this.rotating) this.stopRotation();
      this.scheduleRotation();
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.clearIdleTimer();
        if (this.rotating) this.stopRotation();
        return;
      }
      // volta a contar do zero (spec §3.11) — nao reaproveita o tempo que
      // já tinha passado antes de a aba ficar oculta
      this.scheduleRotation();
    },
    scheduleRotation() {
      this.clearIdleTimer();
      if (!this.rotationEnabled || this.filterOpen) return;
      this.idleTimer = setTimeout(() => {
        this.idleTimer = null;
        this.startRotation();
      }, IDLE_ROTATION_DELAY_MS);
    },
    clearIdleTimer() {
      if (!this.idleTimer) return;
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    },
    startRotation() {
      if (!this.canRotate) return;
      this.selectedCardId = null;
      this.resetIllustrationView();
      this.rotating = true;
      this.advanceRotation();
      this.rotationInterval.start();
    },
    advanceRotation() {
      if (!this.canRotate) {
        this.stopRotation();
        return;
      }
      const ids = this.assets.map(asset => asset.id);
      const nextId = nextRotationId(ids, this.selectedAssetId, this.rotationIndex);
      if (!nextId) {
        this.stopRotation();
        return;
      }
      // Na fase 1, `ids` é sempre a árvore inteira e `this.selectedAssetId`
      // está sempre nela — então o resume-por-`rotationIndex` dentro de
      // nextRotationId (ver assetRotation.js) nunca é exercitado aqui, só na
      // fase 3 (lista = favoritos, onde o ativo em tela pode não pertencer à
      // lista). `rotationIndex` já é load-bearing hoje por outro caminho —
      // `rotationPositionLabel`, no footer — não o remova achando que é
      // morto.
      this.rotationIndex = ids.indexOf(nextId);
      this.$router.replace({ name: 'assets-view-panel', params: { assetId: nextId } });
    },
    stopRotation() {
      this.rotating = false;
      this.rotationInterval.stop();
      this.rotationRemainingSec = 0;
    },
    onRotationTick(remainingMs) {
      const remainingSec = Math.ceil(remainingMs / 1000);
      if (remainingSec === this.rotationRemainingSec) return;
      this.rotationRemainingSec = remainingSec;
    },
    onCardMouseLeave(id) {
      if (this.hoveredCardId === id) this.hoveredCardId = null;
    },
    onCardClick(id) {
      if (!this.readings) return;
      if (this.selectedCardId === id) {
        this.selectedCardId = null;
        this.resetIllustrationView();
        return;
      }
      this.selectedCardId = id;
      const def = CONNECTOR_DEFS.find(item => item.id === id);
      if (def) this.focusIllustrationOnPart(def.part);
    },
    onCanvasClick() {
      if (!this.selectedCardId) return;
      this.selectedCardId = null;
      this.resetIllustrationView();
    },
    focusIllustrationOnPart(id) {
      const { illustration } = this.$refs;
      if (illustration && typeof illustration.focusOnPart === 'function') illustration.focusOnPart(id);
    },
    resetIllustrationView() {
      const { illustration } = this.$refs;
      if (illustration && typeof illustration.resetView === 'function') illustration.resetView();
    },
    onRefreshTick() {
      this.loadReadings();
    },
    // Desativado por enquanto, junto com o botão (ver __save-framing-button).
    // onSaveFramingClick() {
    //   const { illustration } = this.$refs;
    //   if (illustration && typeof illustration.logCameraFraming === 'function') {
    //     illustration.logCameraFraming();
    //   }
    // },

    loadAssetInfo() {
      if (!this.selectedAssetId) return undefined;
      const requestedAssetId = this.selectedAssetId;
      this.loadingAssetInfo = true;
      return CmaDashboardService.getAssetInfo(requestedAssetId)
        .then(({ registrationData, transformerData, coolingStagesData }) => {
          if (this.selectedAssetId !== requestedAssetId) return;
          this.registrationData = registrationData;
          this.transformerData = transformerData;
          this.coolingStagesData = coolingStagesData;
          this.loadingAssetInfo = false;
        });
    },
    loadReadings() {
      if (!this.selectedAssetId) return undefined;
      const requestedAssetId = this.selectedAssetId;
      const installationId = this.selectedAsset && this.selectedAsset.installation.id;
      // 1ª leitura da tela/ativo: mostra a última data gravada até a leitura
      // atual responder (ou pra sempre, se ela falhar).
      if (!this.lastUpdateAt) {
        this.lastUpdateAt = CmaDashboardService.localGetLastUpdate(requestedAssetId);
      }
      // `this.readings` (leitura anterior) vai como fallback contra card
      // sumindo por uma falha pontual de leitura de módulo (ver
      // cmaCardsAdapter.js, withStaleFallback) — já é `null` aqui quando o
      // ativo mudou (ver watch de selectedAssetId abaixo), então não vaza
      // dado de um ativo pro outro.
      const readingsPromise = CmaDashboardService
        .getReadings(requestedAssetId, installationId, this.readings);
      return readingsPromise.then((readings) => {
        // Troca de ativo enquanto essa leitura ainda estava em voo: descarta
        // a resposta atrasada (seria de um ativo que não é mais o
        // selecionado — mesma guarda já usada em loadAssetInfo acima).
        if (this.selectedAssetId !== requestedAssetId) return;
        this.readings = readings;
        // Só marca a "Última atualização" quando a requisição de cards deu
        // certo (em falha, getReadings rejeita e este then não roda).
        this.lastUpdateAt = new Date();
        CmaDashboardService.localSaveLastUpdate(requestedAssetId, this.lastUpdateAt);
        this.$nextTick(() => this.updateConnectorPaths());
      }).catch(() => {
        // Falha na requisição de cards (fora do ar, sem conexão): mantém a
        // leitura e a "Última atualização" anteriores até a próxima tentativa.
      });
    },
    scheduleConnectorUpdate() {
      if (this.connectorFrame) cancelAnimationFrame(this.connectorFrame);
      this.connectorFrame = requestAnimationFrame(() => this.updateConnectorPaths());
    },
    onViewerAnchors(anchors) {
      this.anchors = anchors;
      this.updateConnectorPaths();
    },
    onViewerSwitching(value) {
      this.switching = value;
    },
    onAssetInfoToggle(expanded) {
      this.assetInfoExpanded = expanded;
      this.$nextTick(this.scheduleConnectorUpdate);
    },
    onAssetInfoIconClick() {
      this.onAssetInfoToggle(!this.assetInfoExpanded);
    },
    onViewerIdleRotate(value) {
      this.isIdleRotating = value;
    },
    onViewerExploded(value) {
      this.exploded = value;
    },
    onExplodeClick() {
      const { illustration } = this.$refs;
      if (!illustration) return;
      if (this.exploded) {
        this.selectedCardId = null;
        this.resetIllustrationView();
        return;
      }
      // o card selecionado NAO e limpo aqui: e ele que mantem a camera
      // enquadrando a peca, e expandir dali e o comportamento pedido — a peca
      // se separa do conjunto na frente de quem pediu o zoom (ver
      // toggleExplosion em CmaAsset3dView.vue)
      if (typeof illustration.toggleExplosion === 'function') illustration.toggleExplosion();
    },
    connectorClass(connector) {
      const isVisible = connector.status !== 'normal' || connector.id === this.activeCardId;
      return [
        `cma-dashboard__connector--${connector.status}`,
        {
          'cma-dashboard__connector--active': connector.id === this.activeCardId,
          'cma-dashboard__connector--hidden': !isVisible,
        },
      ];
    },
    updateConnectorPaths() {
      const hubEl = this.$refs.hub;
      const viewerEl = this.$refs.illustration && this.$refs.illustration.$el;
      if (!this.readings || !hubEl || !viewerEl) {
        this.connectorPaths = [];
        return;
      }
      const hubRect = hubEl.getBoundingClientRect();
      const viewerRect = viewerEl.getBoundingClientRect();
      const anchorsByPart = this.anchors.reduce(
        (acc, anchor) => ({ ...acc, [anchor.id]: anchor }), {},
      );
      this.connectorPaths = CONNECTOR_DEFS.map(({ id, anchor, part }) => {
        const cardComponent = this.$refs[`card-${id}`];
        const cardEl = cardComponent && cardComponent.$el;
        const card = this.cardsById[id];
        const target = anchorsByPart[part];
        /* Card sem módulo no banco entra como 'normal': o status dele é null
         * (ver cardsConfig.js, buildCards) e, sem isso, o `!== 'normal'` de
         * connectorClass acenderia a linha de um card que não tem dado — o
         * oposto de "discreto, sem concorrer com a informação real". */
        const status = (card && card.status) || 'normal';
        if (!cardEl || typeof cardEl.getBoundingClientRect !== 'function' || !target || !target.visible) {
          return {
            id, path: '', status, visible: false, endX: 0, endY: 0,
          };
        }
        const cardRect = cardEl.getBoundingClientRect();
        const startX = (anchor === 'right' ? cardRect.right : cardRect.left) - hubRect.left;
        const startY = (cardRect.top + cardRect.height / 2) - hubRect.top;
        const endX = (viewerRect.left + target.x) - hubRect.left;
        const endY = (viewerRect.top + target.y) - hubRect.top;
        const bendX = startX + (endX - startX) * CONNECTOR_BEND_RATIO;
        return {
          id,
          path: `M ${startX} ${startY} L ${bendX} ${startY} L ${endX} ${endY}`,
          status,
          visible: true,
          endX,
          endY,
        };
      });
    },
  },
  watch: {
    // Os nomes dos cards e dos campos vêm do backend já no idioma da requisição
    // (Accept-Language, ver DicionarioCampo) — trocar o idioma da tela precisa
    // buscar de novo, senão eles ficam no idioma antigo até o próximo refresh.
    // A hierarquia também: o nome do tipo de ativo vem traduzido e é ele que
    // o filtro de transformadores compara (ver filterTransformerAssets).
    '$i18n.locale': function onLocaleChange() {
      this.loadAssetTree().then(() => this.loadReadings());
    },
    selectedAssetId() {
      this.selectedCardId = null;
      this.readings = null;
      this.lastUpdateAt = null;
      this.loadReadings();
      this.registrationData = null;
      this.transformerData = null;
      this.coolingStagesData = null;
      this.loadAssetInfo();
    },
    // Reage à URL mudando por fora do clique na árvore (voltar/avançar do
    // navegador, link direto colado enquanto a tela já está aberta) — a
    // árvore já está carregada nesse ponto, então só reaplica a mesma regra
    // de resolução (ver resolveSelectedAssetId), sem recarregá-la.
    assetId() {
      this.selectedAssetId = this.resolveSelectedAssetId(this.assetTree);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

@keyframes cma-skeleton-shimmer
  0%
    background-position: -150% 0
  100%
    background-position: 150% 0

.cma-skeleton-bar
  $base: var(--color-background-regular)
  $highlight: var(--color-background-regular-hover)
  display: inline-block
  border-radius: 4px
  background: linear-gradient(90deg, $base 25%, $highlight 50%, $base 75%)
  background-size: 200% 100%
  animation: cma-skeleton-shimmer 1.6s ease-in-out infinite

.cma-dashboard
  display: flex
  flex-direction: column
  background-color: var(--color-page-filter-background)
  height: calc(100vh - #{$footer-height})
  @supports (height: 100dvh)
    height: calc(100dvh - #{$footer-height})
  overflow: hidden

  &__header
    display: flex
    align-items: center
    gap: 0.75rem
    padding: 1rem 2rem
    background-color: var(--color-background-light)
    border-bottom: 1px solid var(--color-border-dark)
    flex-shrink: 0

  &__logo
    height: 4.5rem
    width: auto
    flex-shrink: 0

  // Agrupa os icones de acao do cabecalho (rodizio/recarregar/info/filtro)
  // num unico item flex do header: assim o gap do header (0.75rem, pensado
  // para logo/titulo) nao se repete entre os icones, que ficam bem mais
  // proximos entre si.
  &__header-actions
    display: flex
    align-items: center
    gap: 0.125rem
    flex-shrink: 0

    +media-min-3xl()
      gap: 0.25rem

  &__title-row
    display: flex
    align-items: center
    gap: 0.75rem

  // O numero de serie e o titulo sao a mesma tipografia, nos tres degraus de
  // tela. Numa regra so para nao divergirem: eram dois blocos com tamanhos
  // proprios, e o numero ainda tinha a cor cravada em branco, que sumia no
  // tema claro.
  &__title,
  &__serial-number
    color: var(--font-color-primary)
    font-size: 1.5rem
    font-weight: 700

    +media-min-3xl()
      font-size: 1.85rem

    +media-min-4xl()
      font-size: 2.2rem

  &__title
    margin: 0

  &__serial-number
    white-space: nowrap

  &__tags
    display: flex
    flex-wrap: wrap
    gap: 0.35rem
    list-style: none
    margin: 0
    padding: 0

  &__tag
    display: inline-flex
    align-items: center
    gap: 0.25rem
    padding: 5px 10px
    border-radius: 50px
    background-color: var(--color-border-inner)
    color: var(--color-black-87)
    font-size: 0.85rem
    white-space: nowrap

    +media-min-3xl()
      padding: 7px 13px
      font-size: 1.05rem

    +media-min-4xl()
      padding: 9px 16px
      font-size: 1.25rem

  &__tag-skeleton
    min-width: 5rem
    height: 0.85rem

    +media-min-3xl()
      min-width: 7rem
      height: 1.05rem

    +media-min-4xl()
      min-width: 8rem
      height: 1.25rem

  &__header-main
    display: flex
    flex-direction: column
    gap: 0.25rem
    flex: 1
    min-width: 0

  // o ícone de informações do ativo (ver __asset-info-button), o de
  // play/pause do rodízio (ver __rotation-toggle-button) e o de filtro
  // (botão do próprio s-sidebar, ver __filter abaixo) usam a mesma
  // moldura do recarregar: são vizinhos na mesma fileira do cabeçalho
  &__refresh-button,
  &__save-framing-button,
  &__asset-info-button,
  &__rotation-toggle-button,
  &__filter ::v-deep .s-sidebar__button
    display: inline-flex
    align-items: center
    justify-content: center
    width: 1.875rem
    height: 1.875rem
    border: none
    border-radius: 50%
    background: none
    color: var(--font-color-secondary)
    cursor: pointer
    flex-shrink: 0

    +media-min-3xl()
      width: 2.25rem
      height: 2.25rem

    +media-min-4xl()
      width: 2.6rem
      height: 2.6rem

    .material-icons-round
      font-size: 1.375rem

      +media-min-3xl()
        font-size: 1.7rem

      +media-min-4xl()
        font-size: 2rem

    &:hover
      color: var(--font-color-primary)
      background-color: var(--color-background-dark)

    // sem isso, o navegador pinta o proprio fundo/contorno de foco e clique
    // por cima do botao: um circulo branco (fundo nativo do <button>) e uma
    // sombra escura quadrada (outline nativo) que nao acompanham o
    // border-radius: 50% nem o fundo do cabecalho
    &:focus,
    &:active
      outline: none
      box-shadow: none

    &::-moz-focus-inner
      border: 0

  // aberto, o icone so muda de cor: o fundo redondo que havia aqui virava um
  // circulo escuro atras do "i" e lia como sujeira, nao como estado
  &__asset-info-button--active
    color: var(--cma-color-highlight)

  // diferente dos vizinhos (refresh/info/filtro, que ficam neutros e só
  // mudam de cor com hover/estado): o de rodízio é o único que "faz algo
  // sozinho" quando ligado, então tem fundo preenchido sempre — cor pedida
  // pelo solicitante (#A6A6A7, sem variável equivalente já definida no
  // projeto), ícone claro por cima para contraste. O play/pause do ícone já
  // basta para marcar o estado; não precisa de uma cor extra para "ativo"
  // por cima de um fundo que já é preenchido.
  &__rotation-toggle-button
    background-color: #A6A6A7
    color: var(--color-white-100)

    // Círculo menor que o herdado do grupo (1.875/2.25/2.6rem, acima): os
    // vizinhos (refresh/info/filtro) ficam neutros em repouso e só mostram
    // fundo no hover, então o que se vê deles o tempo todo é só o glifo do
    // ícone (1.375/1.7/2rem). Este botão tem fundo preenchido sempre — se
    // usasse o mesmo diâmetro do grupo, o círculo (maior que o glifo dos
    // vizinhos) ia ler visivelmente maior que os outros três em repouso,
    // mesmo com todos ocupando a mesma "caixa" invisível. Aqui o círculo
    // acompanha de perto o tamanho do ícone, não o alvo de toque do grupo.
    width: 1.5rem
    height: 1.5rem

    +media-min-3xl()
      width: 1.85rem
      height: 1.85rem

    +media-min-4xl()
      width: 2.15rem
      height: 2.15rem

    // explícito no ícone, e não só herdado do botão: a regra do grupo
    // __refresh-button/__asset-info-button/__rotation-toggle-button (acima)
    // também estiliza ".material-icons-round" (só o font-size, mas isso
    // basta pra essa regra e a de cor aqui empatarem em especificidade por
    // seletor — sem mirar o ícone direto, a ordem de declaração vira o
    // único critério de desempate, o que é frágil)
    //
    // font-size tambem reduzido (mesma proporcao do circulo acima, 0.8x o
    // do grupo): herdando o 1.375/1.7/2rem do grupo, o glifo enchia o
    // circulo menor deste botao e lia visivelmente maior que os icones dos
    // vizinhos, mesmo os dois tendo a mesma caixa de botao.
    .material-icons-round
      color: var(--color-white-100)
      font-size: 1.1rem

      +media-min-3xl()
        font-size: 1.36rem

      +media-min-4xl()
        font-size: 1.6rem

    // Invertido em relação ao texto comum da tela (que já vai claro no tema
    // escuro e escuro no claro, via --font-color-primary, ver
    // src/styles/defaults/base.sass): aqui é o oposto, a pedido do
    // solicitante — branco no tema claro, preto no tema escuro — contra o
    // fundo cinza fixo (#A6A6A7, igual nos dois temas).
    body[data-theme="dark"] &
      color: var(--color-black-100-no-opacity)

      .material-icons-round
        color: var(--color-black-100-no-opacity)

    &:hover
      background-color: #A6A6A7
      opacity: 0.85

  &__datetime
    color: var(--font-color-primary)
    font-weight: 700

  &__hub
    display: flex
    flex-direction: column
    gap: 1.5rem
    flex: 1
    min-height: 0
    overflow-y: auto

    +media-min-lg()
      flex-direction: row
      align-items: stretch
      position: relative
      overflow: hidden

  &__canvas
    display: flex
    flex-direction: column
    gap: 2.5rem

    +media-min-lg()
      position: relative
      display: block
      flex: 1
      height: 100%
      transition: margin-right 0.28s ease

  &__canvas--with-aside
    +media-min-lg()
      margin-right: calc(260px + 1.5rem)

      .cma-dashboard__card-column--right
        padding-top: 1rem

      // Painel de informações do ativo aberto: o botão precisa de mais
      // respiro à direita (era 32%, ver &__stage-controls) pra não ficar
      // perto demais da borda com o painel visível, nem sobrepor o card de
      // resfriamento (último da coluna direita, ver &__card-column--right).
      .cma-dashboard__stage-controls
        padding-right: 37%

  // O painel nao tem mais barra colapsada: quem o chama e o icone do cabecalho
  // (ver __asset-info-button), e ele so existe no DOM quando esta aberto —
  // entao aqui e sempre a altura cheia, sem os degraus de 3.25/3.5/3.85rem que
  // reservavam espaco para a barra.
  &__asset-info
    +media-min-lg()
      position: absolute
      top: 0
      right: 0
      width: 260px
      height: 100%
      z-index: 4

  &__hub &__asset-info
    +media-min-lg()
      border-top-right-radius: 0

  &__card-column
    +media-min-lg()
      position: absolute
      top: 0
      bottom: 0
      display: flex
      flex-direction: column
      justify-content: space-between
      gap: 0.75rem
      padding: 1rem 0
      z-index: 2

    // tela baixa: a folga entre os 4 cards e o respiro vertical da coluna sao
    // a primeira coisa a ceder (ver media-short em mobile-first.sass)
    +media-short()
      gap: 0.5rem
      padding: 0.5rem 0

    +media-very-short()
      gap: 0.35rem
      padding: 0.25rem 0

    // 3%, e nao 1%: as colunas ficavam quase encostadas na borda da tela. E
    // percentual, como antes, para a folga acompanhar a largura do monitor.
    &--left
      +media-min-lg()
        left: 3%
        align-items: flex-start

    // sem o padding-top de 4.5rem que existia so para desviar da barra de
    // "Informacoes do ativo", que deixou de ocupar o topo da coluna
    &--right
      +media-min-lg()
        right: 3%
        align-items: flex-end
        padding-top: 1rem

  // Largura em `vw` com piso E teto (em vez de um valor fixo por breakpoint):
  // acompanha a largura do monitor continuamente entre os dois limites, sem
  // travar num px a partir de um tamanho de tela — era o que deixava o card
  // longe do transformador em monitores largos (a ilustração escala pela
  // altura do container, não pela largura, então não some esse vão sozinha).
  // O teto de 700px evita o oposto: sem ele, o card cresce sem limite prático
  // em monitores grandes (~27" já basta) e passa a dominar a tela.
  //
  // `clamp()`, e não `max()`: o projeto compila Sass com LibSass (via
  // node-sass), que TEM um `max()` próprio (Sass core) e tenta calcular o
  // valor ele mesmo em vez de repassar como CSS puro — quebra o build com
  // unidades diferentes ("Incompatible units: 'px' and 'vw'"). `clamp()` o
  // LibSass não reconhece, então repassa verbatim pro navegador, que já
  // suporta unidades mistas de verdade. Continua sem resolver de todo
  // monitores muito desproporcionais (ex. 49" ultrawide) — a ilustração
  // segue ancorada por altura, não largura.
  &__card
    +media-min-lg()
      width: clamp(340px, 25vw, 780px)

    // Tela larga e baixa: a coluna nao tem altura para 4 cards nesse tamanho
    // (ver media-short em mobile-first.sass), entao encolhe a largura tambem —
    // o card fica mais compacto no conjunto em vez de vazar da coluna.
    +media-short()
      width: 340px

    +media-very-short()
      width: 320px

  &__stage-controls
    display: flex
    justify-content: center
    order: -1

    +media-min-lg()
      position: absolute
      left: 0
      right: 0
      bottom: 0
      order: 0
      // Direita, não mais centralizado — pedido de produto pra ficar perto
      // do card de resfriamento (último da coluna direita, ver
      // &__card-column--right, `right: 3%`, mesmo respiro daqui). O padding
      // é maior que o `right` do card (era 28%): o card escala em `vw` até
      // 780px (ver &__card) e, com respiro igual, o botão ficava por baixo
      // do canto do card em telas largas — sobreposição visível tanto com o
      // painel de informações do ativo aberto quanto fechado.
      justify-content: flex-end
      padding-right: 33%
      // Reduzido (era clamp(1.25rem, 6vh, 4rem)) pra descer o botão, mais
      // perto da borda inferior — pedido de produto pra dar mais respiro
      // acima, junto do deslocamento do ativo (ver &__asset-illustration).
      padding-bottom: clamp(0.5rem, 3vh, 2rem)
      pointer-events: none
      z-index: 2

    // numa tela baixa os 6vh valem pouco, mas o que sobra de altura e
    // disputado: cola o botao mais perto da base
    +media-very-short()
      padding-bottom: 0.4rem

  // O botao inteiro sai do `font-size`: altura, folga e espacamento estao em
  // `em`. E `vmin`, e nao `vw`, porque os breakpoints do projeto sao todos de
  // largura (ver mobile-first.sass) e um notebook 1920x1080 cai no mesmo
  // degrau de um monitor grande, apesar de ter bem menos altura.
  &__explode-button
    display: inline-flex
    align-items: center
    justify-content: center
    gap: 0.4em
    pointer-events: auto
    min-height: 2.5em
    padding: 0.55em 1.35em
    border: 1px solid var(--color-border-dark)
    border-radius: 4px
    background-color: var(--color-background-light)
    color: var(--font-color-secondary)
    font-size: clamp(0.85rem, 1.45vmin, 1.4rem)
    font-weight: 700
    letter-spacing: 0.05em
    text-transform: uppercase
    white-space: nowrap
    cursor: pointer
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35)

    // o `vmin` acima ja desce com a altura; aqui e o teto que precisa descer,
    // senao numa ultrawide baixa o botao cresce pela largura e some o modelo
    +media-short()
      font-size: clamp(0.8rem, 1.15vmin, 1.05rem)

    .material-icons-round
      font-size: 1.15em

    &:hover
      border-color: var(--color-warning)
      color: var(--font-color-primary)

    &--active
      border-color: var(--color-warning)
      color: var(--color-warning)

  &__asset-image
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    align-self: center
    width: 100%
    order: -1

    +media-min-lg()
      position: absolute
      inset: 0
      transform: none
      order: 0
      width: auto
      z-index: 1

  &__connectors
    display: none
    pointer-events: none
    transition: opacity 0.2s ease

    +media-min-lg()
      display: block
      position: absolute
      inset: 0
      width: 100%
      height: 100%
      z-index: 3

  &__connectors--hidden
    opacity: 0

  &__connector-line
    fill: none
    stroke: var(--color-border-dark)
    stroke-width: 1.5
    transition: opacity 0.2s ease

  &__connector-halo
    fill: currentColor
    opacity: 0.2

  &__connector-dot
    fill: currentColor

  &__connector--normal
    color: var(--color-success)

  &__connector--attention
    color: var(--color-warning)

  &__connector--critical
    color: var(--color-danger)

  &__connector--active .cma-dashboard__connector-line
    stroke: var(--color-warning)

  &__connector--attention .cma-dashboard__connector-line
    stroke: var(--color-warning)

  &__connector--critical .cma-dashboard__connector-line
    stroke: var(--color-danger)

  &__connector--hidden
    opacity: 0
    transition: opacity 0.2s ease

  &__asset-illustration
    width: 100%
    max-width: 100%
    position: relative
    z-index: 1

    aspect-ratio: 723 / 540
    height: auto

    +media-min-lg()
      aspect-ratio: 1856 / 976
      height: auto
      max-height: 100%
      // Sobe o ativo um pouco (pedido de produto) — a ilustração é
      // centralizada verticalmente no espaço do canvas (ver
      // &__asset-image), então um deslocamento aqui não briga com o layout
      // absoluto dos cards/conectores, que continuam medindo a partir da
      // posição original do container.
      transform: translateY(-4%)

  &__footer
    display: flex
    align-items: center
    justify-content: space-between
    padding: 0.75rem 2rem
    background-color: var(--color-background-light)
    border-top: 1px solid var(--color-border-dark)
    flex-shrink: 0

  &__update-indicator
    display: flex
    align-items: center
    gap: 0.375rem
    color: var(--font-color-secondary)
    font-size: 0.8rem
    letter-spacing: 0.01em

    +media-min-3xl()
      font-size: 1rem

    +media-min-4xl()
      font-size: 1.2rem

  &__update-icon
    font-size: 1rem

    +media-min-3xl()
      font-size: 1.25rem

    +media-min-4xl()
      font-size: 1.5rem

  &__rotation-indicator
    display: flex
    align-items: center
    gap: 0.375rem
    color: var(--font-color-secondary)
    font-size: 0.8rem

    +media-min-3xl()
      font-size: 1rem

    +media-min-4xl()
      font-size: 1.2rem

  &__rotation-icon
    font-size: 1rem

    +media-min-3xl()
      font-size: 1.25rem

    +media-min-4xl()
      font-size: 1.5rem
</style>
